import type { ServicePluginContext } from "@brains/plugins";
import { formatLabel, pluralizeLabel } from "@brains/utils/string-utils";
import {
  getArrayElement,
  getKind,
  getObjectShape,
  hasStringFormat,
  readEnumValues,
  readLiteralValue,
  readMetadata,
  unwrapField,
} from "@brains/utils/zod-introspect";
import { isRecord } from "@brains/utils/is-record";
// Base-note entity type id (mirrors NOTE_ENTITY_TYPE in @brains/entity-service,
// which plugins may not import directly and @brains/plugins does not re-export).
const NOTE_ENTITY_TYPE = "note";

/**
 * Per-entity-type display metadata accepted by the editor API.
 * Structurally compatible with `EntityDisplayEntry` from `@brains/plugins` —
 * shell callers can pass their full registry map without conversion.
 */
export interface EntityDisplayLabel {
  label?: string | undefined;
}

export type StudioEntityDisplayMap = Partial<
  Record<string, EntityDisplayLabel>
>;

/**
 * Field widget descriptor the editor form renderer consumes.
 * (Inherited from the Sveltia widget vocabulary; now first-party.)
 */
export interface StudioFieldCondition {
  field: string;
  value: unknown;
}

export interface StudioFieldWidget {
  name: string;
  label: string;
  widget: string;
  required?: boolean;
  default?: unknown;
  options?: string[];
  condition?: StudioFieldCondition;
  field?: StudioFieldWidget;
  fields?: StudioFieldWidget[];
}

const LONG_TEXT_FIELDS = new Set([
  "description",
  "excerpt",
  "summary",
  "tagline",
  "story",
]);

/**
 * Notes use whole-document editing unless their type participates in a
 * registered grouping. Participation exposes the effective Properties schema
 * for every note, including notes with no membership yet.
 */
export function isRawEntityType(
  entityType: string,
  entities: Pick<ServicePluginContext["entities"], "isGroupingContributor">,
): boolean {
  return (
    entityType === NOTE_ENTITY_TYPE &&
    !entities.isGroupingContributor(entityType)
  );
}

/** How a type's entries nest, as Studio presents and creates them. */
export interface StudioTypeHierarchy {
  /** What a level of the hierarchy is called. */
  kind: "page" | "folder";
  /** Whether new entries may be created inside a folder. */
  nested: boolean;
}

/**
 * How Studio nests a type's entries. Site content is organised in pages.
 * Directory-sync exports notes as one segment at its root, so new notes are
 * created there rather than inside a folder.
 */
export function studioTypeHierarchy(entityType: string): StudioTypeHierarchy {
  return {
    kind: entityType === "site-content" ? "page" : "folder",
    nested: entityType !== NOTE_ENTITY_TYPE,
  };
}

/**
 * Resolve the display labels for an entity type, honouring any
 * entityDisplay label. A site's pluralName is the path its pages live under,
 * not a name: book sections open under /books but are sections.
 */
export function entityTypeLabels(
  entityType: string,
  display?: EntityDisplayLabel,
): { label: string; pluralLabel: string } {
  if (entityType === "grouping-definitions")
    return { label: display?.label ?? "Groupings", pluralLabel: "Groupings" };
  const defaultLabel =
    entityType === NOTE_ENTITY_TYPE ? "Note" : formatLabel(entityType);
  const label = display?.label ?? defaultLabel;
  return { label, pluralLabel: pluralizeLabel(label) };
}

function readStudioCondition(
  schema: unknown,
): StudioFieldCondition | undefined {
  const condition = readMetadata(schema)?.["studioCondition"];
  if (
    !isRecord(condition) ||
    typeof condition["field"] !== "string" ||
    !Object.hasOwn(condition, "value")
  ) {
    return undefined;
  }
  return { field: condition["field"], value: condition["value"] };
}

/** Presentations a schema can ask Studio for, by `studioWidget` metadata. */
const STUDIO_WIDGETS = [
  // People assigned from the brain's members.
  "members",
  // People named, by name only.
  "people",
  // Facts about where an entry came from: shown read-only, never edited.
  "provenance",
  // Bookkeeping no one reads or edits: left off the form.
  "hidden",
] as const;

function readStudioWidget(
  schema: unknown,
): (typeof STUDIO_WIDGETS)[number] | undefined {
  const widget = readMetadata(schema)?.["studioWidget"];
  return STUDIO_WIDGETS.find((candidate) => candidate === widget);
}

/**
 * String fields holding image-entity ids follow the <role>ImageId naming
 * convention (coverImageId, ogImageId, plain imageId).
 */
function isImageReferenceField(name: string): boolean {
  return name === "imageId" || name.endsWith("ImageId");
}

/**
 * Map a single Zod field to a form widget descriptor
 */
export function zodFieldToStudioWidget(
  name: string,
  fieldSchema: unknown,
): StudioFieldWidget {
  const { inner, required, defaultValue } = unwrapField(fieldSchema);
  const kind = getKind(inner);
  const effectiveDefault = defaultValue ?? readLiteralValue(inner);
  const condition = readStudioCondition(fieldSchema);

  const base: StudioFieldWidget = {
    name,
    label: formatLabel(name),
    widget: "string",
    ...(!required && { required: false }),
    ...(effectiveDefault !== undefined && { default: effectiveDefault }),
    ...(condition && { condition }),
  };

  const studioWidget = readStudioWidget(fieldSchema);
  if (studioWidget) return { ...base, widget: studioWidget };

  switch (kind) {
    case "string": {
      if (isImageReferenceField(name)) {
        return { ...base, widget: "image" };
      }
      if (hasStringFormat(inner, "datetime")) {
        return { ...base, widget: "datetime" };
      }
      if (hasStringFormat(inner, "date")) {
        return { ...base, widget: "date" };
      }
      if (LONG_TEXT_FIELDS.has(name)) {
        return { ...base, widget: "text" };
      }
      return { ...base, widget: "string" };
    }
    case "number":
      return { ...base, widget: "number" };
    case "boolean":
      return { ...base, widget: "boolean" };
    case "enum": {
      const options = readEnumValues(inner);
      return { ...base, widget: "select", ...(options ? { options } : {}) };
    }
    case "array": {
      const elementType = getArrayElement(inner);
      const elementWidget = zodFieldToStudioWidget("item", elementType);
      if (elementWidget.widget === "object" && elementWidget.fields) {
        return { ...base, widget: "list", fields: elementWidget.fields };
      }
      return {
        ...base,
        widget: "list",
        field: { name, label: formatLabel(name), widget: elementWidget.widget },
      };
    }
    case "object": {
      const fields = Object.entries(getObjectShape(inner) ?? {}).map(
        ([key, value]) => zodFieldToStudioWidget(key, value),
      );
      return { ...base, widget: "object", fields };
    }
    // A record or an unknown value cannot be typed as text: it reads as
    // structure instead of a blank box that would overwrite it.
    case "record":
    case "unknown":
    case "any":
      return { ...base, widget: "object" };
    case "literal":
      return { ...base, widget: "string" };
    default:
      return { ...base, widget: "string" };
  }
}
